import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Users, Plus, Trash, Pencil, Funnel, FileArrowUp, Eye, MagnifyingGlass, CheckCircle,
} from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

const emptyFilter = {
  status: [],
  fonte: [],
  province: [],
  has_marketing_consent: null,
  has_email: true,
  search: '',
};

export default function MarketingGroupsPage() {
  const [groups, setGroups] = useState([]);
  const [options, setOptions] = useState({ statuses: [], fonti: [], provinces: [] });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // group object or 'new'
  const [form, setForm] = useState({
    name: '', description: '', source_type: 'filter',
    filter_criteria: { ...emptyFilter }, manual_lead_ids: [], external_contacts: [],
  });
  const [preview, setPreview] = useState({ count: 0, members: [] });
  const [previewing, setPreviewing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [detail, setDetail] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [gRes, oRes] = await Promise.all([
        fetch(`${API}/api/marketing/groups`, { credentials: 'include' }),
        fetch(`${API}/api/marketing/filters/options`, { credentials: 'include' }),
      ]);
      if (gRes.ok) setGroups(await gRes.json());
      if (oRes.ok) setOptions(await oRes.json());
    } catch (e) { toast.error('Errore caricamento gruppi'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const runPreview = async () => {
    setPreviewing(true);
    try {
      const res = await fetch(`${API}/api/marketing/groups/preview`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          filter_criteria: form.filter_criteria,
          manual_lead_ids: form.manual_lead_ids,
          external_contacts: form.external_contacts,
        }),
      });
      const data = await res.json();
      if (res.ok) setPreview(data);
      else toast.error(data.detail || 'Errore preview');
    } catch (e) { toast.error('Errore preview'); }
    finally { setPreviewing(false); }
  };

  const saveGroup = async () => {
    if (!form.name.trim()) return toast.error('Nome obbligatorio');
    try {
      const isNew = editing === 'new';
      const url = isNew ? `${API}/api/marketing/groups` : `${API}/api/marketing/groups/${editing.id}`;
      const method = isNew ? 'POST' : 'PUT';
      const res = await fetch(url, {
        method, credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (res.ok) {
        toast.success(isNew ? 'Gruppo creato' : 'Gruppo aggiornato');
        setEditing(null);
        fetchData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Errore salvataggio');
      }
    } catch (e) { toast.error('Errore salvataggio'); }
  };

  const deleteGroup = async (id) => {
    if (!window.confirm('Eliminare questo gruppo?')) return;
    try {
      const res = await fetch(`${API}/api/marketing/groups/${id}`, { method: 'DELETE', credentials: 'include' });
      if (res.ok) { toast.success('Eliminato'); fetchData(); }
    } catch (e) { toast.error('Errore'); }
  };

  const openGroupDetail = async (id) => {
    try {
      const res = await fetch(`${API}/api/marketing/groups/${id}`, { credentials: 'include' });
      if (res.ok) setDetail(await res.json());
    } catch (e) { toast.error('Errore'); }
  };

  const importContactsFile = async (file) => {
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API}/api/marketing/groups/import-contacts`, {
        method: 'POST', credentials: 'include', body: fd,
      });
      const data = await res.json();
      if (res.ok) {
        setForm({ ...form, external_contacts: [...(form.external_contacts || []), ...data.contacts] });
        toast.success(`${data.count} contatti importati`);
      } else {
        toast.error(data.detail || 'Errore import');
      }
    } catch (e) { toast.error('Errore import'); }
    finally { setImporting(false); }
  };

  const toggleMulti = (key, value) => {
    const arr = form.filter_criteria[key] || [];
    const next = arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value];
    setForm({ ...form, filter_criteria: { ...form.filter_criteria, [key]: next } });
  };

  const openNew = () => {
    setForm({
      name: '', description: '', source_type: 'filter',
      filter_criteria: { ...emptyFilter }, manual_lead_ids: [], external_contacts: [],
    });
    setPreview({ count: 0, members: [] });
    setEditing('new');
  };

  const openEdit = (g) => {
    setForm({
      name: g.name || '', description: g.description || '',
      source_type: g.source_type || 'filter',
      filter_criteria: g.filter_criteria || { ...emptyFilter },
      manual_lead_ids: g.manual_lead_ids || [],
      external_contacts: g.external_contacts || [],
    });
    setPreview({ count: 0, members: [] });
    setEditing(g);
  };

  if (loading) return <div className="p-8 text-slate-500">Caricamento...</div>;

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="marketing-groups-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
            <Users size={32} weight="duotone" className="text-orange-500" />
            Gruppi Marketing
          </h1>
          <p className="text-slate-500 mt-1">Crea gruppi da filtri, selezione manuale o import contatti esterni.</p>
        </div>
        <Button onClick={openNew} data-testid="create-group-btn" className="bg-orange-500 hover:bg-orange-600">
          <Plus size={18} className="mr-2" /> Nuovo gruppo
        </Button>
      </div>

      {groups.length === 0 ? (
        <div className="bg-white rounded-lg border border-slate-200 p-12 text-center">
          <Users size={64} className="mx-auto text-slate-300 mb-4" weight="duotone" />
          <p className="text-slate-500">Nessun gruppo creato. Inizia creando il primo.</p>
        </div>
      ) : (
        <div className="grid gap-3">
          {groups.map(g => (
            <div key={g.id} className="bg-white rounded-lg border border-slate-200 p-4 hover:shadow-sm transition">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-semibold text-slate-900">{g.name}</h3>
                    <Badge variant="outline" className="text-xs">{g.source_type}</Badge>
                  </div>
                  {g.description && <p className="text-sm text-slate-600 mt-1">{g.description}</p>}
                  <div className="flex gap-4 mt-2 text-sm">
                    <span className="text-slate-700"><strong>{g.member_count}</strong> destinatari</span>
                    <span className="text-slate-500">{g.email_count} email</span>
                    <span className="text-slate-400 text-xs">Creato {new Date(g.created_at).toLocaleDateString('it-IT')}</span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => openGroupDetail(g.id)} data-testid={`view-group-${g.id}`}>
                    <Eye size={16} />
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => openEdit(g)}>
                    <Pencil size={16} />
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => deleteGroup(g.id)}
                          className="text-red-600 hover:bg-red-50">
                    <Trash size={16} />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* CREATE/EDIT DIALOG */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing === 'new' ? 'Nuovo gruppo' : `Modifica gruppo`}</DialogTitle>
          </DialogHeader>

          <div className="space-y-5 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Nome gruppo</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                       placeholder="Es: Clienti Lazio Pre-contratto" data-testid="group-name-input" />
              </div>
              <div>
                <Label>Descrizione</Label>
                <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                       placeholder="Facoltativa" />
              </div>
            </div>

            <div className="border rounded-lg p-4 bg-slate-50">
              <div className="flex items-center gap-2 mb-3">
                <Funnel size={18} className="text-orange-500" />
                <h4 className="font-semibold">Filtri sui leads esistenti</h4>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">Status</Label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {options.statuses.map(s => (
                      <Badge key={s}
                             variant={form.filter_criteria.status?.includes(s) ? 'default' : 'outline'}
                             onClick={() => toggleMulti('status', s)}
                             className="cursor-pointer">{s}</Badge>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Fonte</Label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {options.fonti.map(f => (
                      <Badge key={f}
                             variant={form.filter_criteria.fonte?.includes(f) ? 'default' : 'outline'}
                             onClick={() => toggleMulti('fonte', f)}
                             className="cursor-pointer">{f}</Badge>
                    ))}
                  </div>
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">Province</Label>
                  <div className="flex flex-wrap gap-2 mt-1 max-h-24 overflow-y-auto">
                    {options.provinces.map(p => (
                      <Badge key={p}
                             variant={form.filter_criteria.province?.includes(p) ? 'default' : 'outline'}
                             onClick={() => toggleMulti('province', p)}
                             className="cursor-pointer">{p}</Badge>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Ricerca testuale (rag.soc./email/P.IVA)</Label>
                  <Input value={form.filter_criteria.search || ''}
                         onChange={(e) => setForm({ ...form, filter_criteria: { ...form.filter_criteria, search: e.target.value } })}
                         placeholder="Filtro libero" />
                </div>
                <div className="flex items-center gap-4 pt-6">
                  <label className="flex items-center gap-2 text-sm">
                    <Checkbox checked={form.filter_criteria.has_email}
                              onCheckedChange={(v) => setForm({ ...form, filter_criteria: { ...form.filter_criteria, has_email: !!v } })} />
                    Solo con email
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <Checkbox checked={form.filter_criteria.has_marketing_consent === true}
                              onCheckedChange={(v) => setForm({ ...form, filter_criteria: { ...form.filter_criteria, has_marketing_consent: v ? true : null } })} />
                    Solo con consenso marketing
                  </label>
                </div>
              </div>
            </div>

            <div className="border rounded-lg p-4 bg-blue-50/40">
              <div className="flex items-center gap-2 mb-3">
                <FileArrowUp size={18} className="text-blue-600" />
                <h4 className="font-semibold">Contatti esterni (nome + email da Excel/CSV)</h4>
              </div>
              <input type="file" accept=".xlsx,.xls,.csv"
                     onChange={(e) => e.target.files[0] && importContactsFile(e.target.files[0])}
                     className="text-sm" disabled={importing}
                     data-testid="import-contacts-input" />
              {form.external_contacts?.length > 0 && (
                <div className="mt-2 text-sm text-slate-700">
                  <strong>{form.external_contacts.length}</strong> contatti esterni aggiunti
                  <Button size="sm" variant="ghost" className="ml-2 text-red-600"
                          onClick={() => setForm({ ...form, external_contacts: [] })}>
                    Rimuovi tutti
                  </Button>
                </div>
              )}
            </div>

            <div className="flex items-center gap-3">
              <Button onClick={runPreview} variant="outline" disabled={previewing} data-testid="preview-group-btn">
                <MagnifyingGlass size={16} className="mr-2" />
                {previewing ? 'Calcolo...' : 'Anteprima destinatari'}
              </Button>
              {preview.count > 0 && (
                <span className="text-sm"><CheckCircle size={14} className="inline text-green-600 mr-1" />
                  <strong>{preview.count}</strong> destinatari univoci</span>
              )}
            </div>

            {preview.members?.length > 0 && (
              <div className="border rounded max-h-60 overflow-y-auto text-sm">
                <table className="w-full">
                  <thead className="bg-slate-100 sticky top-0">
                    <tr>
                      <th className="text-left p-2">Email</th>
                      <th className="text-left p-2">Ragione sociale</th>
                      <th className="text-left p-2">Consenso</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.members.slice(0, 100).map((m, i) => (
                      <tr key={i} className="border-t">
                        <td className="p-2">{m.to_email}</td>
                        <td className="p-2">{m.ragione_sociale}</td>
                        <td className="p-2">{m.consenso_marketing ? 'Sì' : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {preview.members.length > 100 && <p className="p-2 text-xs text-slate-500">... e altri {preview.members.length - 100}</p>}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Annulla</Button>
            <Button onClick={saveGroup} data-testid="save-group-btn" className="bg-orange-500 hover:bg-orange-600">
              Salva gruppo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DETAIL DIALOG */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{detail?.name}</DialogTitle></DialogHeader>
          {detail && (
            <div>
              <p className="text-sm text-slate-500 mb-3">{detail.description}</p>
              <p className="mb-2"><strong>{detail.member_count}</strong> destinatari</p>
              <div className="border rounded max-h-96 overflow-y-auto text-sm">
                <table className="w-full">
                  <thead className="bg-slate-100 sticky top-0">
                    <tr>
                      <th className="text-left p-2">Email</th>
                      <th className="text-left p-2">Ragione sociale</th>
                      <th className="text-left p-2">Origine</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.members?.map((m, i) => (
                      <tr key={i} className="border-t">
                        <td className="p-2">{m.to_email}</td>
                        <td className="p-2">{m.ragione_sociale}</td>
                        <td className="p-2 text-xs text-slate-500">{m.lead_id ? 'Lead' : 'Esterno'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
